import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState
} from "@angular/cdk/layout";
import { Component, inject } from "@angular/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { MenuItem, ToastMessageOptions } from "primeng/api";
import { deployment, environment } from "../../../environments/environment";
import { Role } from "../../_models/role";
import { LoggingService, LogLevel } from "../../_services/logging.service";
import { UserService } from "../../_services/user.service";
import { ReviewSessionService } from "../../_services/review-session.service";
import { ToastModule } from "primeng/toast";
import { TrackingConsentComponent } from "../../tracking-consent/tracking-consent.component";
import { MenubarModule } from "primeng/menubar";
import { MainMenuSearchComponent } from "../main-menu-search/main-menu-search.component";
import { MessageModule } from "primeng/message";
import { LoginComponent } from "../../login/login.component";
import { ScrollTopModule } from "primeng/scrolltop";
import { FooterComponent } from "../footer/footer.component";
import { CommonModule } from "@angular/common";
import { BackendStatusNotificationComponent } from "../../shared/backend-status-notification/backend-status-notification.component";

@Component({
  selector: "app-main",
  templateUrl: "./main.component.html",
  styleUrls: ["./main.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    BackendStatusNotificationComponent,
    FooterComponent,
    LoginComponent,
    TrackingConsentComponent,
    ToastModule,
    MainMenuSearchComponent,
    MenubarModule,
    MessageModule,
    RouterModule,
    ScrollTopModule,
    ToastModule
  ]
})
export class MainComponent {
  userService = inject(UserService);
  /** Reviewer-link session (#226). Used to hide login-oriented chrome from anonymous reviewers. */
  reviewSession = inject(ReviewSessionService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private breakpointObserver = inject(BreakpointObserver);
  private loggingService = inject(LoggingService);

  static readonly ROUTE_DATA_BREADCRUMB = "breadcrumb";
  readonly home = { icon: "fa fa-compass", url: "/home" };
  breadCrumbs: MenuItem[] = [this.home];
  items: MenuItem[];
  wrapSearch: Boolean = false;
  requiresAuth: Boolean = environment.homeRequiresAuth;
  loginMessage: ToastMessageOptions = environment.loginMessage;
  apiUrl: string = deployment.apiPath;

  constructor() {
    this.loggingService.log(
      "MainComponent",
      "requiresAuth: " + this.requiresAuth,
      LogLevel.DEBUG
    );
    this.createMenuForUser();
    this.breakpointObserver
      .observe([Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
      .subscribe((result: BreakpointState) => {
        if (result.matches) {
          this.loggingService.log(
            "MainComponent",
            "Medium to large breakpoint matched: Not wrapping search!",
            LogLevel.DEBUG
          );
          this.wrapSearch = false;
        }
      });
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .subscribe((result: BreakpointState) => {
        if (result.matches) {
          this.loggingService.log(
            "MainComponent",
            "Small breakpoint matched: Wrapping search!",
            LogLevel.DEBUG
          );
          this.wrapSearch = true;
        }
      });
  }

  private createMenuForUser() {
    this.userService.isAuthenticated$.subscribe(observable => {
      if (observable) {
        this.loggingService.log(
          "MainComponent",
          "User is authenticated.",
          LogLevel.DEBUG
        );
        this.items = [
          {
            label: "Home",
            icon: "fa fa-compass",
            routerLink: ["/home"],
            visible: environment.features.home
          },
          {
            label: "Lipids",
            icon: "fa fa-search",
            routerLink: ["/lipid"],
            visible: environment.features.lipid
          },
          {
            label: "Studies",
            icon: "fa fa-vials",
            routerLink: ["/study"],
            visible: environment.features.study
          },
          {
            label: "Datasets",
            icon: "fa fa-archive",
            routerLink: ["/dataset"],
            visible: environment.features.dataset
          },
          {
            label: "mzTab-M",
            icon: "fa fa-file-alt",
            routerLink: ["/mztabm"],
            visible: environment.features.mztab
          },
          {
            label: "News",
            icon: "fas fa-newspaper",
            routerLink: ["/news"],
            visible: environment.features.news
          },
          {
            label: "Help",
            icon: "fa fa-solid fa-circle-question",
            routerLink: ["/guide"],
            visible: environment.features.docs
          },
          {
            label: "About",
            icon: "fa fa-solid fa-circle-info",
            routerLink: ["/info"],
            visible: environment.features.about
          },
          {
            label: "API",
            icon: "fa fa-solid fa-scroll",
            url: this.apiUrl,
            visible: environment.features.api
          }
        ];
        if (
          this.userService.hasRole(Role.Curator) ||
          this.userService.hasRole(Role.Admin)
        ) {
          this.items.push({
            label: "Curate",
            icon: "fas fa-highlighter",
            routerLink: ["/curate"],
            visible: environment.features.curate
          });
        }
        if (
          this.userService.hasRole(Role.Curator) ||
          this.userService.hasRole(Role.Admin)
        ) {
          // A curator (without the admin role) can only reach the submissions
          // page inside /admin (see AdminComponent's megamenu filtering); the
          // nav entry still needs to be discoverable for them to get there.
          this.items.push({
            label: "Admin",
            icon: "fas fa-tools",
            routerLink: ["/admin"],
            visible: environment.features.admin
          });
        }
        if (this.userService.hasRole(Role.User)) {
          this.items.push({
            label: "Submissions",
            icon: "fas fa-cloud-upload-alt",
            routerLink: ["/submit"],
            visible: environment.features.submit
          });
        }
        this.items.push({
          label: "User",
          icon: "fas fa-user",
          routerLink: ["/login"],
          visible: environment.features.login
        });
      } else {
        this.loggingService.log(
          "MainComponent",
          "User is not authenticated.",
          LogLevel.DEBUG
        );
        if (this.requiresAuth) {
          this.items = [
            {
              label: "Home",
              icon: "fa fa-compass",
              routerLink: ["/"],
              visible: environment.features.home
            },
            {
              label: "API",
              icon: "fa fa-solid fa-scroll",
              url: this.apiUrl,
              visible: environment.features.api
            },
            {
              label: "Login",
              icon: "fa fa-sign-in-alt",
              routerLink: ["/login"],
              visible: environment.features.login
            }
          ];
        } else {
          this.items = [
            {
              label: "Home",
              icon: "fa fa-compass",
              routerLink: ["/home"],
              visible: environment.features.home
            },
            {
              label: "Lipids",
              icon: "fa fa-search",
              routerLink: ["/lipid"],
              visible: environment.features.lipid
            },
            {
              label: "Studies",
              icon: "fa fa-vials",
              routerLink: ["/study"],
              visible: environment.features.study
            },
            {
              label: "Datasets",
              icon: "fa fa-archive",
              routerLink: ["/dataset"],
              visible: environment.features.dataset
            },
            {
              label: "News",
              icon: "fas fa-newspaper",
              routerLink: ["/news"],
              visible: environment.features.news
            },
            {
              label: "Help",
              icon: "fa fa-solid fa-circle-question",
              routerLink: ["/guide"],
              visible: environment.features.docs
            },
            {
              label: "About",
              icon: "fa fa-solid fa-circle-info",
              routerLink: ["/info"],
              visible: environment.features.about
            },
            {
              label: "API",
              icon: "fa fa-solid fa-scroll",
              url: this.apiUrl,
              visible: environment.features.api
            },
            {
              label: "Login",
              icon: "fa fa-sign-in-alt",
              routerLink: ["/login"],
              visible: environment.features.login
            }
          ];
        }
      }
    });
  }
}
